import { ENDPOINT_PATH } from "../config";
import { authFetch } from "../utils/authFetch";

/**
 * @param {String} artworkName
 * @param {String} selectedArtist
 * @param {String} category
 * @param {String} description
 * @param {File} artImage
 */
const addArt = async (artworkName, selectedArtist, category, description, artImage) => {

    const splitName = selectedArtist.split(' ');
    const firstName = splitName[0];
    const lastName = splitName[1];

    description = description ?? "";

    const artFormData = new FormData();
    artFormData.append('artworkName', artworkName);
    artFormData.append('artistFirstName', firstName);
    artFormData.append('artistLastName', lastName);
    artFormData.append('category', category);

    artFormData.append('artDescription', description);
    artFormData.append('artImage', artImage);

    return fetch(ENDPOINT_PATH + `/api/add-art`, {
        method: 'POST',
        body: artFormData
    })
        .then(async (response) => {
            const data = await response.json().catch(() => ({}));
            console.log("Art added: ", data);
            if (!response.ok) {
                throw new Error(data.message || data || 'Failed to add artwork');
            }
            return data;
        })
        .catch((e) => {
            console.error(e);
            throw e;
        });
}
export { addArt };


/**
 * @param {String} artDocID
 * @param {String} artistDocID
 * @param {String} selectedArtist
 * @param {File} artImage
 */
const editArt = async (artDocID, artistDocID, selectedArtist, artImage, updates = {}) => {
    try {
        selectedArtist = selectedArtist ?? null; // Set to null if not passed for logic
        artImage = artImage ?? null;

        const formData = new FormData();

        // These variables are required for the logic
        formData.append('artDocID', artDocID);
        formData.append('artistDocID', artistDocID);
        if (selectedArtist != null)
            formData.append('selectedArtist', selectedArtist);
        if (artImage != null)
            formData.append('artImage', artImage);

        // This adds in all of the optional variables
        for (const key in updates) {
            formData.append(key, updates[key]);
        }

        const response = await fetch(ENDPOINT_PATH + `/api/edit-art`, {
            method: 'PATCH',
            body: formData
        });

        const data = await response.json();

        if (response.ok) {
            console.log('Art edited successfully:', data);
            return data;
        } else {
            console.error('Error editing art:', data);
            throw new Error(data.message || 'Failed to edit artwork');
        }
    } catch (error) {
        console.error("Error occurred in editArt:", error);
        throw error;
    }
}
export { editArt };

/**
 * @param {String} artistDocID
 * @param {String} artDocID
 */
const delArt = async (artistDocID, artDocID, reason) => {
    try {
        const response = await authFetch(ENDPOINT_PATH + `/api/del-art`, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                'artistDocID': artistDocID,
                'artDocID': artDocID,
                'reason': reason
            })
        });

        const data = await response.json();

        if (response.ok) {
            console.log("Art deleted successfully:", data);
            return data;
        } else {
            console.error("Error deleting art:", data);
            throw new Error(data.message || 'Failed to delete artwork');
        }
    } catch (error) {
        console.error("Error in delArt:", error);
        throw error;
    }
}
export { delArt };


/**
 * @param {String} category
 */
const addCategory = async (category) => {

    fetch(ENDPOINT_PATH + `/api/add-category`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newCategory: category })
    })
        .then(response => response.json())
        .then(data => {
            console.log("Art category added");
        })
        .catch(e => { console.error(e); });
}
export { addCategory };


const getCategories = async () => {

    return fetch(ENDPOINT_PATH + `/api/get-categories`, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(data => {
            console.log("Categories returned successfully: ", data);
            return data
        })
        .catch(e => { console.error(e); throw e; });
}
export { getCategories };



/**
 * Get all artwork
 * @param {Boolean} adminView - If true, returns all artwork (for admin portal). If false/undefined, only returns approved artwork (for public view)
 */
const getAllArt = async (adminView = false) => {
    const adminViewParam = adminView ? '?adminView=true' : '';

    return fetch(ENDPOINT_PATH + `/api/get-all-art${adminViewParam}`, {
        method: 'GET'
    })
        .then(response => response.json())
        .then(data => {
            console.log("All art returned successfully: ", data);
            return data;
        })
        .catch(e => { console.error(e); throw e; });

}
export { getAllArt };


/**
 * @param {String} artistID
 */
const getArtistArt = async (artistID) => {
    return fetch(ENDPOINT_PATH + `/api/get-artists-art?artistID=${artistID}`, {
        method: 'GET',
    })
        .then(response => response.json())
        .then(data => {
            console.log("Artists art successfully returned");
            return data;
        })
        .catch(e => { console.error(e); throw e });
}
export { getArtistArt };



/**
 * @param {String} category
 */
const getCategoryArt = async (category) => {

    return fetch(ENDPOINT_PATH + `/api/get-category-art?category=${category}`, {
        method: 'GET',
    })
        .then(response => response.json())
        .then(data => {
            console.log("Category art returned successfully: ", data);
            return data;
        })
        .catch(e => { console.error(e); throw e });
}
export { getCategoryArt };

/**
 * Update artwork status (approve/reject) - Admin only
 * @param {String} adminUid - Firebase Auth user ID of the admin
 * @param {String} artistDocId - Artist document ID
 * @param {String} artDocId - Artwork document ID
 * @param {String} newStatus - "approved" | "rejected"
 * @returns Promise with response data
 */
const updateArtStatus = async (adminUid, artistDocId, artDocId, newStatus, reason = null) => {
    try {
        const response = await authFetch(ENDPOINT_PATH + '/api/admin/update-art-status', {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                artistDocId: artistDocId,
                artDocId: artDocId,
                newStatus: newStatus,
                reason: reason
            })
        });

        const data = await response.json();

        if (response.ok) {
            console.log("Artwork status updated successfully:", data);
            return data;
        } else {
            console.error("Error updating artwork status:", data);
            throw new Error(data.message || 'Failed to update artwork status');
        }
    } catch (error) {
        console.error("Error in updateArtStatus:", error);
        throw error;
    }
};

/**
 * Update artist application status (approve/reject/pending) - Admin only
 * @param {String} adminUid - Firebase Auth user ID of the admin
 * @param {String} applicationId - Application document ID
 * @param {String} newStatus - "approved" | "rejected" | "pending"
 * @returns Promise with response data
 */
const updateApplicationStatus = async (adminUid, applicationId, newStatus) => {
    try {
        const response = await authFetch(ENDPOINT_PATH + '/api/admin/update-application-status', {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                applicationId: applicationId,
                newStatus: newStatus
            })
        });

        const data = await response.json();

        if (response.ok) {
            console.log("Application status updated successfully:", data);
            return data;
        } else {
            console.error("Error updating application status:", data);
            throw new Error(data.message || 'Failed to update application status');
        }
    } catch (error) {
        console.error("Error in updateApplicationStatus:", error);
        throw error;
    }
};

export {  updateArtStatus, updateApplicationStatus };